import React, { useEffect, useState } from 'react'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Button } from '../../../components/Button/Button'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { Link, useNavigate } from 'react-router-dom'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import './Settings.scss'

export const ConsentSettings: React.FC = () => {
  useDocumentTitle('Setup')
  const accountId = useAppSelector(selectAccountId)
  const navigate = useNavigate()
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateAccount] = useUpdateAccountMutation()
  const { hasQuota } = useZocksQuota()
  const userConsentTrackingAvailable = hasQuota('ai.userConsentTracking.value')
  const consentApprovalCheckAvailable = hasQuota('room.consentApprovalCheck.value')
  const [userConsentTrackingEnabled, setUserConsentTrackingEnabled] = useState<boolean>(false)
  const [userConsentManualOverride, setUserConsentManualOverride] = useState<boolean>(false)
  const [consentApprovalCheckEnabled, setConsentApprovalCheckEnabled] = useState<boolean>(false)
  const [userConsentTrackingEnterpriseControlled, setUserConsentTrackingEnterpriseControlled] = useState<boolean>(false)
  const [consentApprovalCheckEnterpriseControlled, setConsentApprovalCheckEnterpriseControlled] = useState<boolean>(false)
  const userConsentTrackingChanged = async (checked: boolean) => {
    setUserConsentTrackingEnabled(checked)
    if (!account) {
      return
    }
    const accountConfig = { ...account.data.config, userConsentTrackingEnabled: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const consentApprovalCheckChanged = async (checked: boolean) => {
    setConsentApprovalCheckEnabled(checked)
    if (!account) {
      return
    }
    const accountConfig = { ...account.data.config, consentApprovalCheck: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const userConsentManualOverrideChanged = async (checked: boolean) => {
    setUserConsentManualOverride(checked)
    if (!account) {
      return
    }
    const accountConfig = { ...account.data.config, userConsentManualOverride: checked }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  useEffect(() => {
    if (!account) {
      return
    }
    setUserConsentTrackingEnabled(account.data.effectiveConfig.complianceConfig.userConsentTrackingEnabled || false)
    setUserConsentManualOverride(account.data.effectiveConfig.complianceConfig.userConsentManualOverride || false)
    setUserConsentTrackingEnterpriseControlled(account.data.enterpriseConfig.complianceConfig.userConsentTrackingEnabled != null)
    setConsentApprovalCheckEnabled(account.data.effectiveConfig.complianceConfig.consentApprovalCheck || false)
    setConsentApprovalCheckEnterpriseControlled(account.data.enterpriseConfig.complianceConfig.consentApprovalCheck != null)
  }, [account])

  return (
    <>
      <Container className='d-flex flex-column align-items-start zocks-gap-2'>
        <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <Stack direction='horizontal' gap={3}>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  {' '}
                  Zocks can optionally keep a record of you obtaining verbal consent with your clients to use an automated notetaker. The results can be tracked
                  and viewed on the{' '}
                  <Link className='zocks-text-gray-900 text-decoration-underline' to='/library/manage-contacts'>
                    Contacts
                  </Link>{' '}
                  page.
                </span>
              </div>
              {!userConsentTrackingAvailable && (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button
                    className='d-flex p-1 p-2 align-items-center text-nowrap'
                    variant='primary'
                    onClick={() => {
                      navigate('/account/plan')
                    }}
                  >
                    <img alt='Upgrade' src={PrizeBadge} />
                    Get feature
                  </Button>
                </ScopedBlock>
              )}
            </Stack>
          </Col>
        </Row>
        {userConsentTrackingEnterpriseControlled ? (
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>User consent tracking is managed at the enterprise level.</span>
              </div>
            </Col>
          </Row>
        ) : null}
        <Row className='gap-3 p-0 align-items-center'>
          <Col xs='auto' className='p-0'>
            <div className='paragraph-medium'>I want to track user consent</div>
          </Col>
          <Col className='p-0'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Form.Switch
                disabled={isAccountFetching || !userConsentTrackingAvailable || userConsentTrackingEnterpriseControlled}
                checked={userConsentTrackingEnabled}
                onChange={(e) => userConsentTrackingChanged(e.currentTarget.checked)}
              />
            </ScopedBlock>
          </Col>
        </Row>
        {consentApprovalCheckEnterpriseControlled ? (
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>Disabling meetings without Client consent is managed at the enterprise level.</span>
              </div>
            </Col>
          </Row>
        ) : null}
        <Row className='gap-3 p-0 align-items-center'>
          <Col xs='auto' className='p-0'>
            <div className='paragraph-medium'>Disable meetings without Client consent</div>
          </Col>
          <Col className='p-0'>
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <ScopedBlock
                quota={{
                  quota: 'room.consentApprovalCheck.value',
                }}
                missingScopeAction={MissingScopeAction.UPGRADE}
                upgradeContainerClassName='d-flex'
              >
                <Form.Switch
                  disabled={isAccountFetching || !consentApprovalCheckAvailable || consentApprovalCheckEnterpriseControlled}
                  checked={consentApprovalCheckEnabled}
                  onChange={(e) => consentApprovalCheckChanged(e.currentTarget.checked)}
                />
              </ScopedBlock>
            </ScopedBlock>
          </Col>
        </Row>
        {userConsentTrackingEnabled ? (
          <Row className='w-100'>
            <Col className='p-4 justify-content-center rounded zocks-border-gray-200'>
              <Row className='mb-3'>
                <Col className='p-0'>
                  <h5 className='zocks-text-gray-900'>Manual consent management</h5>
                </Col>
              </Row>
              <Row className='p-3 mb-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
                <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
                  <Stack direction='horizontal' gap={3}>
                    <div className='d-flex align-self-center gap-3 align-items-center'>
                      <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                      <span className='zocks-text-gray-900'>Manually record client consent when it's not already present.</span>
                    </div>
                  </Stack>
                </Col>
              </Row>
              <Row>
                <Col xs='auto' className='p-0'>
                  <div className='paragraph-medium'>I want to enable manual user consent management</div>
                </Col>
                <Col>
                  <ScopedBlock
                    scope={{
                      scope: 'account:update',
                      superScope: ['account:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Form.Switch
                      disabled={isAccountFetching || !userConsentTrackingAvailable || userConsentTrackingEnterpriseControlled || !userConsentTrackingEnabled}
                      checked={userConsentManualOverride}
                      onChange={(e) => userConsentManualOverrideChanged(e.currentTarget.checked)}
                    />
                  </ScopedBlock>
                </Col>
              </Row>
            </Col>
          </Row>
        ) : null}
      </Container>
    </>
  )
}
